import React, {
  useCallback,
  useMemo,
  useState,
  forwardRef,
  useRef,
} from 'react'
import { GestureResponderEvent } from 'react-native'
import { useFragment, graphql } from 'react-relay'
import type { ReferenceType } from '@floating-ui/react'

import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import {
  useSelectItemParentContext,
  useSelectContext,
  ViewProps,
  composeRefs,
  TamaguiElement,
  TamaguiTextElement,
} from 'tamagui'

import { Trans, useLingui } from '@lingui/react/macro'
import { useFlashMessage } from '../DynamicIsland/useFlashMessage'
import { useMutation } from '../../data/useMutation'

import {
  StyledSelect,
  StyledSelectContent,
  StyledSelectItem,
  StyledSelectItemText,
  StyledSelectViewport,
} from '../Form/FormSelect'

import { Paragraph } from '../Text'

import {
  EventAttendButtonButton,
  AnimateChevronProvider,
} from './EventAttendButtonButton'

import { EventAttendButtonNew_event$key } from './__generated__/EventAttendButtonNew_event.graphql'
import {
  EventAttendButtonNew_query$data,
  EventAttendButtonNew_query$key,
} from './__generated__/EventAttendButtonNew_query.graphql'

import { EventAttendButtonSingleAttend_event$key } from './__generated__/EventAttendButtonSingleAttend_event.graphql'
import { EventAttendButtonHybridAttend_event$key } from './__generated__/EventAttendButtonHybridAttend_event.graphql'

import { EventAttendButtonSingleAttendMutation } from './__generated__/EventAttendButtonSingleAttendMutation.graphql'
import { EventAttendButtonSingleRemoveAttendanceMutation } from './__generated__/EventAttendButtonSingleRemoveAttendanceMutation.graphql'

import {
  EventAttendButtonHybridAttendMutation,
  EventAttendeeStatus,
} from './__generated__/EventAttendButtonHybridAttendMutation.graphql'
import { EventAttendButtonHybridRemoveAttendanceMutation } from './__generated__/EventAttendButtonHybridRemoveAttendanceMutation.graphql'

import { EventAttendButtonSelectTriggerButton_event$key } from './__generated__/EventAttendButtonSelectTriggerButton_event.graphql'

import '../Button/Button.css'

const getBackgroundLocation = (location: Location): Location => {
  const state = location.state as { backgroundLocation?: Location } | null

  return state?.backgroundLocation || location
}

export const EventAttendButton = forwardRef<
  TamaguiElement,
  {
    event: EventAttendButtonNew_event$key
    query: EventAttendButtonNew_query$key
    onOpenChange?: (open: boolean) => void
    animateChevron?: boolean
  } & ViewProps
>(({ event, query, onOpenChange, animateChevron, ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment EventAttendButtonNew_event on Event {
        rowId
        prettyUrl

        endAt

        hasExternalUrl
        hasVenue

        myAttendance {
          visibleStatus
          userId
          paymentStatus
        }

        owner {
          __typename
          ... on Guild {
            slugId

            myMembership {
              membershipLevel
              status
            }
          }
        }

        ...EventAttendButtonButton_event

        ...EventAttendButtonSingleAttend_event
        ...EventAttendButtonHybridAttend_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventAttendButtonNew_query on Query {
        viewer {
          id
        }
      }
    `,
    query
  )

  const navigate = useNavigate()
  const location = useLocation()

  const eventInPast = useMemo(() => {
    if (!data.endAt) {
      return false
    }

    return new Date().getTime() > new Date(data.endAt).getTime()
  }, [data.endAt])

  const content = (() => {
    if (eventInPast) {
      if (
        !queryData.viewer ||
        (data.owner?.__typename === 'Guild' && !data.owner.myMembership)
      ) {
        return (
          <EventAttendButtonButton
            {...props}
            ref={ref as React.Ref<TamaguiElement>}
            errorFlash={false}
            event={data}
            onPress={(event) => {
              if (data.owner?.__typename === 'Guild') {
                event.preventDefault()
                event.stopPropagation()

                const slugId = data.owner.slugId

                const guildConfirmURL = `/${slugId}/confirm`

                if (!queryData.viewer) {
                  navigate(guildConfirmURL, {
                    state: {
                      backgroundLocation: getBackgroundLocation(location),
                    },
                  })
                } else {
                  navigate(guildConfirmURL, {
                    state: {
                      backgroundLocation: getBackgroundLocation(location),
                    },
                  })
                }
              }
            }}
          />
        )
      }

      return (
        <EventAttendButtonButton
          {...props}
          ref={ref as React.Ref<TamaguiElement>}
          event={data}
          errorFlash={false}
        />
      )
    }

    if (data.hasExternalUrl && data.hasVenue) {
      return (
        <HybridAttendButton
          {...props}
          queryData={queryData}
          event={data}
          ref={ref}
          onOpenChange={onOpenChange}
        />
      )
    }

    if (!data.hasExternalUrl && !data.hasVenue) {
      return (
        <Paragraph
          ref={ref as React.Ref<TamaguiTextElement>}
          size='$4'
          fontWeight='$5'
          color='$errorColor'
        >
          <Trans>Missing Venue or Online URL</Trans>
        </Paragraph>
      )
    }

    return (
      <SingleAttendButton
        {...props}
        queryData={queryData}
        event={data}
        ref={ref}
        onOpenChange={onOpenChange}
      />
    )
  })()

  if (animateChevron) {
    return <AnimateChevronProvider>{content}</AnimateChevronProvider>
  }

  return content
})
EventAttendButton.displayName = 'EventAttendButton'

const SingleAttendButton = forwardRef<
  TamaguiElement,
  {
    event: EventAttendButtonSingleAttend_event$key
    queryData: EventAttendButtonNew_query$data
    onOpenChange?: (open: boolean) => void
  }
>(({ event, onOpenChange, queryData, ...props }, ref) => {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment EventAttendButtonSingleAttend_event on Event {
        rowId
        prettyUrl

        hasVenue
        hasExternalUrl

        myAttendance {
          visibleStatus
          paymentStatus
        }

        owner {
          __typename

          ... on Guild {
            slugId
            myMembership {
              membershipLevel
              status
            }
          }
        }

        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          totalCount
        }

        attendeeForm {
          rowId
        }

        ...EventAttendButtonButton_event
        ...EventAttendButtonSelectTriggerButton_event
      }
    `,
    event
  )

  const { showFlash } = useFlashMessage()

  const eventAttend = useMutation<EventAttendButtonSingleAttendMutation>(
    graphql`
      mutation EventAttendButtonSingleAttendMutation(
        $input: AttendEventInput!
      ) {
        attendEvent(input: $input) {
          event {
            ...EventItemNew_event

            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `
  )

  const removeEventAttendance =
    useMutation<EventAttendButtonSingleRemoveAttendanceMutation>(graphql`
      mutation EventAttendButtonSingleRemoveAttendanceMutation(
        $input: RemoveEventAttendanceInput!
      ) {
        removeEventAttendance(input: $input) {
          event {
            ...EventItemNew_event

            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `)

  const navigate = useNavigate()
  const location = useLocation()

  const [buttonForcedState, setButtonForcedState] = useState<
    'pressed' | 'success' | 'error' | 'notPressed'
  >('notPressed')

  const onPress = useCallback(
    async (event: GestureResponderEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (
        data.owner?.__typename === 'Guild' &&
        data.owner.myMembership?.status === 'PENDING'
      ) {
        showFlash(t`Guild Membership Pending`, 'error')
        return
      }

      if (
        data.eventTicketingTiers.totalCount > 0 &&
        data.myAttendance?.paymentStatus === 'SUCCEEDED'
      ) {
        return
      }

      setButtonForcedState('pressed')

      try {
        if (
          !queryData.viewer ||
          (data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
          data.owner?.__typename !== 'Guild' ||
          data.eventTicketingTiers.totalCount > 0
        ) {
          const status = data.hasVenue
            ? 'ATTENDING_IN_PERSON'
            : 'ATTENDING_VIRTUAL'

          const eventAttendURL = `/events/${data.prettyUrl}/attend?status=${status}`

          if (!queryData.viewer) {
            const isGuildEvent = data.owner?.__typename === 'Guild'
            const guildSlugId =
              data.owner?.__typename === 'Guild' ? data.owner.slugId : undefined

            if (isGuildEvent && guildSlugId) {
              navigate(
                `/${guildSlugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
                {
                  state: {
                    backgroundLocation: getBackgroundLocation(location),
                  },
                }
              )
            } else {
              navigate('/sign-up', {
                state: {
                  backgroundLocation: getBackgroundLocation(location),
                  authRedirectPath: eventAttendURL,
                },
              })
            }
          } else if (
            data.owner?.__typename === 'Guild' &&
            !data.owner.myMembership
          ) {
            // Authenticated non-member: go through guild confirm first
            const guildSlugId = data.owner.slugId
            navigate(
              `/${guildSlugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
              {
                state: {
                  backgroundLocation: getBackgroundLocation(location),
                },
              }
            )
          } else if (data.eventTicketingTiers.totalCount > 0) {
            // Ticketed events: navigate to full attend screen (not dialog)
            // so users can see tier selection inline
            navigate(eventAttendURL)
          } else {
            navigate(eventAttendURL, {
              state: {
                backgroundLocation: getBackgroundLocation(location),
              },
            })
          }
        } else if (
          !data.myAttendance ||
          data.myAttendance.visibleStatus === 'NOT_ATTENDING'
        ) {
          const status = data.hasVenue
            ? 'ATTENDING_IN_PERSON'
            : 'ATTENDING_VIRTUAL'

          // If event has an attendee form, navigate to attend dialog (shows form)
          if (data.attendeeForm) {
            const eventAttendURL = `/events/${data.prettyUrl}/attend?status=${status}`
            navigate(eventAttendURL, {
              state: {
                backgroundLocation: getBackgroundLocation(location),
              },
            })
          } else {
            await eventAttend({
              variables: {
                input: {
                  eventId: data.rowId,
                  status,
                },
              },
            })

            setButtonForcedState('success')

            await new Promise<void>((resolve) =>
              setTimeout(() => resolve(), 2000)
            )
          }
        }
      } catch (err) {
        setButtonForcedState('error')

        await new Promise<void>((resolve) => setTimeout(() => resolve(), 2000))
      }

      setButtonForcedState('notPressed')
    },
    [
      queryData,
      eventAttend,
      data,
      navigate,
      location,
      setButtonForcedState,
      showFlash,
      t,
    ]
  )

  if (
    data.eventTicketingTiers.totalCount > 0 ||
    !data.myAttendance ||
    ['NOT_ATTENDING'].includes(data.myAttendance.visibleStatus)
  ) {
    return (
      <EventAttendButtonButton
        ref={ref}
        {...props}
        event={data}
        forceState={buttonForcedState}
        onPress={onPress}
      />
    )
  }

  if (data.myAttendance.visibleStatus === 'PENDING_APPROVAL') {
    return (
      <EventAttendButtonButton
        ref={ref}
        {...props}
        event={data}
        errorFlash={false}
      />
    )
  }

  return (
    <StyledSelect
      // ref={(ref) => {
      //   if (!ref) {
      //     return
      //   }

      //   ref.ontouchstart = (e) => {
      //     e.preventDefault()
      //   }
      // }}
      onOpenChange={onOpenChange}
      onValueChange={async (value: EventAttendeeStatus) => {
        setButtonForcedState('pressed')

        try {
          if (value === 'NOT_ATTENDING') {
            await removeEventAttendance({
              variables: {
                input: {
                  eventId: data.rowId,
                },
              },
            })

            setButtonForcedState('success')
          } else {
            setButtonForcedState('error')
          }
        } catch (err) {
          setButtonForcedState('error')
        } finally {
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 2000)
          )

          setButtonForcedState('notPressed')
        }
      }}
    >
      <SelectTriggerButton
        ref={ref}
        {...props}
        buttonForcedState={buttonForcedState}
        event={data}
      />

      <StyledSelectContent zIndex={200_000}>
        <StyledSelectViewport>
          <StyledSelectItem index={1} value='NOT_ATTENDING'>
            <StyledSelectItemText>
              <Trans>Don&apos;t Attend</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>
        </StyledSelectViewport>
      </StyledSelectContent>
    </StyledSelect>
  )
})
SingleAttendButton.displayName = 'SingleAttendButton'

const HybridAttendButton = forwardRef<
  TamaguiElement,
  {
    event: EventAttendButtonHybridAttend_event$key
    queryData: EventAttendButtonNew_query$data
    onOpenChange?: (open: boolean) => void
  }
>(({ event, onOpenChange, queryData, ...props }, ref) => {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment EventAttendButtonHybridAttend_event on Event {
        rowId
        prettyUrl

        hasVenue
        hasExternalUrl

        myAttendance {
          visibleStatus
          paymentStatus
        }

        owner {
          __typename

          ... on Guild {
            slugId
            myMembership {
              membershipLevel
              status
            }
          }
        }

        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          totalCount
        }

        attendeeForm {
          rowId
        }

        ...EventAttendButtonButton_event
        ...EventAttendButtonSelectTriggerButton_event
      }
    `,
    event
  )

  const { showFlash } = useFlashMessage()

  const eventAttend = useMutation<EventAttendButtonHybridAttendMutation>(
    graphql`
      mutation EventAttendButtonHybridAttendMutation(
        $input: AttendEventInput!
      ) {
        attendEvent(input: $input) {
          event {
            ...EventItemNew_event
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `
  )

  const removeEventAttendance =
    useMutation<EventAttendButtonHybridRemoveAttendanceMutation>(graphql`
      mutation EventAttendButtonHybridRemoveAttendanceMutation(
        $input: RemoveEventAttendanceInput!
      ) {
        removeEventAttendance(input: $input) {
          event {
            ...EventItemNew_event
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `)

  const navigate = useNavigate()
  const location = useLocation()

  const [buttonForcedState, setButtonForcedState] = useState<
    'pressed' | 'success' | 'error' | 'notPressed'
  >('notPressed')

  // Controlled value so we can reset after navigating away (e.g. for auth).
  // Without this, selecting the same option twice doesn't fire onValueChange.
  const [selectValue, setSelectValue] = useState('')

  const selectOptions = useMemo(() => {
    if (
      !data.myAttendance ||
      data.myAttendance?.visibleStatus === 'NOT_ATTENDING'
    ) {
      return (
        <>
          <StyledSelectItem index={1} value='ATTENDING_IN_PERSON'>
            <StyledSelectItemText>
              <Trans>Attend In-Person</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>

          <StyledSelectItem index={2} value='ATTENDING_VIRTUAL'>
            <StyledSelectItemText>
              <Trans>Attend Online</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>
        </>
      )
    }

    if (data.myAttendance.visibleStatus === 'PENDING_APPROVAL') {
      return null
    }

    if (
      ['ATTENDING_IN_PERSON', 'WAITING_IN_PERSON'].includes(
        data.myAttendance.visibleStatus
      )
    ) {
      return (
        <>
          <StyledSelectItem index={1} value='ATTENDING_VIRTUAL'>
            <StyledSelectItemText>
              <Trans>Attend Online</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>

          <StyledSelectItem index={2} value='NOT_ATTENDING'>
            <StyledSelectItemText>
              <Trans>Don&apos;t Attend</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>
        </>
      )
    }

    if (
      ['ATTENDING_VIRTUAL', 'WAITING_VIRTUAL'].includes(
        data.myAttendance.visibleStatus
      )
    ) {
      return (
        <>
          <StyledSelectItem index={1} value='ATTENDING_IN_PERSON'>
            <StyledSelectItemText>
              <Trans>Attend In-Person</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>

          <StyledSelectItem index={2} value='NOT_ATTENDING'>
            <StyledSelectItemText>
              <Trans>Don&apos;t Attend</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>
        </>
      )
    }

    return (
      <StyledSelectItem index={1} value='NOT_ATTENDING'>
        <StyledSelectItemText>
          <Trans>Don&apos;t Attend</Trans>
        </StyledSelectItemText>
      </StyledSelectItem>
    )
  }, [data])

  if (data.myAttendance?.visibleStatus === 'PENDING_APPROVAL') {
    return (
      <EventAttendButtonButton
        ref={ref}
        {...props}
        event={data}
        errorFlash={false}
      />
    )
  }

  if (
    data.eventTicketingTiers.totalCount > 0 &&
    data.myAttendance?.paymentStatus === 'SUCCEEDED'
  ) {
    return (
      <EventAttendButtonButton
        ref={ref}
        {...props}
        event={data}
        errorFlash={false}
      />
    )
  }

  return (
    <StyledSelect
      // ref={(ref) => {
      //   if (typeof ref !== 'object' || !ref) {
      //     return
      //   }

      //   ref.ontouchstart = (e) => {
      //     e.preventDefault()
      //   }
      // }}
      value={selectValue}
      onOpenChange={onOpenChange}
      onValueChange={async (value: EventAttendeeStatus) => {
        if (
          data.owner?.__typename === 'Guild' &&
          data.owner.myMembership?.status === 'PENDING'
        ) {
          showFlash(t`Guild Membership Pending`, 'error')
          setSelectValue('')
          return
        }

        setButtonForcedState('pressed')

        try {
          if (
            !queryData.viewer ||
            (data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
            data.owner?.__typename !== 'Guild' ||
            data.eventTicketingTiers.totalCount > 0
          ) {
            const eventAttendURL = `/events/${data.prettyUrl}/attend?status=${value}`

            if (!queryData.viewer) {
              const isGuildEvent = data.owner?.__typename === 'Guild'
              const guildSlugId =
                data.owner?.__typename === 'Guild'
                  ? data.owner.slugId
                  : undefined

              if (isGuildEvent && guildSlugId) {
                navigate(
                  `/${guildSlugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
                  {
                    state: {
                      backgroundLocation: getBackgroundLocation(location),
                    },
                  }
                )
              } else {
                navigate('/sign-up', {
                  state: {
                    backgroundLocation: getBackgroundLocation(location),
                    authRedirectPath: eventAttendURL,
                  },
                })
              }
            } else if (
              data.owner?.__typename === 'Guild' &&
              !data.owner.myMembership
            ) {
              // Authenticated non-member: go through guild confirm first
              const guildSlugId = data.owner.slugId
              navigate(
                `/${guildSlugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
                {
                  state: {
                    backgroundLocation: getBackgroundLocation(location),
                  },
                }
              )
            } else if (data.eventTicketingTiers.totalCount > 0) {
              // Ticketed events: navigate to full attend screen (not dialog)
              // so users can see tier selection inline
              navigate(eventAttendURL)
            } else {
              navigate(eventAttendURL, {
                state: {
                  backgroundLocation: getBackgroundLocation(location),
                },
              })
            }

            // Reset so the same option can be selected again after dialog close
            setSelectValue('')
          } else if (
            (!data.myAttendance ||
              data.myAttendance.visibleStatus === 'NOT_ATTENDING') &&
            (value === 'ATTENDING_IN_PERSON' || value === 'ATTENDING_VIRTUAL')
          ) {
            // If event has an attendee form, navigate to attend dialog (shows form)
            if (data.attendeeForm) {
              const eventAttendURL = `/events/${data.prettyUrl}/attend?status=${value}`
              navigate(eventAttendURL, {
                state: {
                  backgroundLocation: getBackgroundLocation(location),
                },
              })

              // Reset so the same option can be selected again after dialog close
              setSelectValue('')
            } else {
              await eventAttend({
                variables: {
                  input: {
                    eventId: data.rowId,
                    status: value,
                  },
                },
              })

              setButtonForcedState('success')

              await new Promise<void>((resolve) =>
                setTimeout(() => resolve(), 2000)
              )
            }
          } else if (
            data.myAttendance &&
            [
              'ATTENDING_IN_PERSON',
              'ATTENDING_VIRTUAL',
              'WAITING_IN_PERSON',
              'WAITING_VIRTUAL',
            ].includes(data.myAttendance.visibleStatus)
          ) {
            setButtonForcedState('pressed')

            if (value === 'NOT_ATTENDING') {
              await removeEventAttendance({
                variables: {
                  input: {
                    eventId: data.rowId,
                  },
                },
              })
            } else {
              await eventAttend({
                variables: {
                  input: {
                    eventId: data.rowId,
                    status: value,
                  },
                },
              })
            }

            setButtonForcedState('success')

            await new Promise<void>((resolve) =>
              setTimeout(() => resolve(), 2000)
            )
          }
        } catch (err) {
          setButtonForcedState('error')

          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 2000)
          )
        } finally {
          setButtonForcedState('notPressed')
        }
      }}
    >
      <SelectTriggerButton
        ref={ref}
        {...props}
        buttonForcedState={buttonForcedState}
        event={data}
      />

      <StyledSelectContent zIndex={200_000}>
        <StyledSelectViewport>{selectOptions}</StyledSelectViewport>
      </StyledSelectContent>
    </StyledSelect>
  )
})
HybridAttendButton.displayName = 'HybridAttendButton'

const SelectTriggerButton = forwardRef<
  TamaguiElement,
  {
    buttonForcedState: 'pressed' | 'success' | 'error' | 'notPressed'
    event: EventAttendButtonSelectTriggerButton_event$key
  }
>(({ buttonForcedState, event, ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment EventAttendButtonSelectTriggerButton_event on Event {
        ...EventAttendButtonButton_event
      }
    `,
    event
  )

  const selectContext = useSelectContext()

  const buttonRef = useRef<TamaguiElement>(null)

  const itemParentContext = useSelectItemParentContext()

  const referenceProps =
    itemParentContext?.interactions?.getReferenceProps() as
      | Record<string, unknown>
      | undefined
  const setFloatingReference = useCallback(
    (node: TamaguiElement | null) => {
      selectContext?.floatingContext?.refs.setReference(
        node as unknown as ReferenceType | null
      )
    },
    [selectContext]
  )

  return (
    <EventAttendButtonButton
      ref={composeRefs(ref, setFloatingReference, buttonRef)}
      {...props}
      {...referenceProps}
      forceState={buttonForcedState}
      event={data}
      errorFlash={false}
      onPress={(e: GestureResponderEvent) => {
        e.preventDefault()
        e.stopPropagation()
        itemParentContext?.setOpen(!selectContext?.open)
      }}
    />
  )
})
SelectTriggerButton.displayName = 'SelectTriggerButton'
